import type { ImportBatchRow, ImportTarget } from '@discovr/types';
import {
  isDroppedRow,
  isDuplicateRow,
  isImportableRow,
  REVIEW_STATUS_RANK,
  reviewRowStatus,
  type ReviewRowStatus,
} from './reviewRowStatus';

export type ReviewTab = 'all' | 'needs-fixing' | 'duplicates' | 'ready';

export interface DuplicateGroup {
  usernameKey: string;
  rows: ImportBatchRow[];
}

/** Why a row was removed without the admin asking: it repeats a kept row, or
 *  it was hidden in the uploaded workbook. */
export type AutoRemovedReason = 'exact_copy' | 'hidden_in_sheet';

/** A row the server removed on its own; Restore brings it back. Named for the
 *  exact copies it first held, it also lists the rows hidden in the sheet. */
export interface ExactCopy {
  row: ImportBatchRow;
  reason: AutoRemovedReason;
  /** Lowest live row sharing the username; null when that original is gone,
   *  and for a hidden row, which copies nobody. */
  copyOfRowNumber: number | null;
}

export interface ReviewTabContents {
  all: ImportBatchRow[];
  needsFixing: ImportBatchRow[];
  duplicateGroups: DuplicateGroup[];
  exactCopies: ExactCopy[];
  ready: ImportBatchRow[];
}

const NEEDS_FIXING_STATUSES: readonly ReviewRowStatus[] = ['needs-fix', 'in-campaign'];
const FIRST_READY_RANK = REVIEW_STATUS_RANK['will-update'];

type StatusByRow = Map<ImportBatchRow, ReviewRowStatus>;
type RowComparator = (a: ImportBatchRow, b: ImportBatchRow) => number;

function byRowNumber(a: ImportBatchRow, b: ImportBatchRow): number {
  return a.rowNumber - b.rowNumber;
}

function byRankThenRow(statuses: StatusByRow): RowComparator {
  return (a, b) => {
    const rankGap = rankOf(statuses, a) - rankOf(statuses, b);
    if (rankGap !== 0) return rankGap;
    return byRowNumber(a, b);
  };
}

function rankOf(statuses: StatusByRow, row: ImportBatchRow): number {
  const status = statuses.get(row) ?? reviewRowStatus(row);
  return REVIEW_STATUS_RANK[status];
}

function autoRemovedReason(row: ImportBatchRow): AutoRemovedReason | null {
  if (row.dropReason === 'exact_copy' || row.dropReason === 'hidden_in_sheet') return row.dropReason;
  return null;
}

function isLiveRow(row: ImportBatchRow): boolean {
  return !isDroppedRow(row);
}

function needsFixingRow(statuses: StatusByRow) {
  return (row: ImportBatchRow): boolean => {
    const status = statuses.get(row);
    return status !== undefined && NEEDS_FIXING_STATUSES.includes(status);
  };
}

function readyRow(statuses: StatusByRow) {
  return (row: ImportBatchRow): boolean => rankOf(statuses, row) >= FIRST_READY_RANK;
}

function lowestRowNumber(group: DuplicateGroup): number {
  return Math.min(...group.rows.map((row) => row.rowNumber));
}

// Membership follows group size, not status: an errored row that is also a
// duplicate ranks as "needs-fix" but its group must still be complete.
function buildDuplicateGroups(liveRows: ImportBatchRow[]): DuplicateGroup[] {
  const membersByKey = new Map<string, ImportBatchRow[]>();
  for (const row of liveRows.filter(isDuplicateRow)) {
    const members = membersByKey.get(row.usernameKey) ?? [];
    members.push(row);
    membersByKey.set(row.usernameKey, members);
  }
  return [...membersByKey.entries()]
    .map(([usernameKey, members]) => ({ usernameKey, rows: members.sort(byRowNumber) }))
    .sort((a, b) => lowestRowNumber(a) - lowestRowNumber(b));
}

function lowestRowNumberByKey(liveRows: ImportBatchRow[]): Map<string, number> {
  const lowest = new Map<string, number>();
  for (const row of liveRows) {
    const known = lowest.get(row.usernameKey);
    if (known === undefined || row.rowNumber < known) lowest.set(row.usernameKey, row.rowNumber);
  }
  return lowest;
}

function buildExactCopies(rows: ImportBatchRow[], liveRows: ImportBatchRow[]): ExactCopy[] {
  const originals = lowestRowNumberByKey(liveRows);
  return rows
    .filter((row) => autoRemovedReason(row) !== null)
    .sort(byRowNumber)
    .map((row) => {
      const reason = autoRemovedReason(row) ?? 'exact_copy';
      return { row, reason, copyOfRowNumber: reason === 'exact_copy' ? (originals.get(row.usernameKey) ?? null) : null };
    });
}

// Outreach never updates or keeps a creator already in the campaign, so its
// Ready tab lists only what the import will add (matching the footer count);
// "Already added" rows stay under All rows.
function readyRows(liveRows: ImportBatchRow[], statuses: StatusByRow, target: ImportTarget): ImportBatchRow[] {
  if (target === 'outreach') return liveRows.filter(isImportableRow);
  return liveRows.filter(readyRow(statuses));
}

export function buildReviewTabs(rows: ImportBatchRow[], target: ImportTarget = 'pipeline'): ReviewTabContents {
  const liveRows = rows.filter(isLiveRow).sort(byRowNumber);
  const statuses: StatusByRow = new Map(liveRows.map((row) => [row, reviewRowStatus(row)]));

  return {
    all: liveRows,
    needsFixing: liveRows.filter(needsFixingRow(statuses)).sort(byRankThenRow(statuses)),
    duplicateGroups: buildDuplicateGroups(liveRows),
    exactCopies: buildExactCopies(rows, liveRows),
    ready: readyRows(liveRows, statuses, target),
  };
}

function duplicateRowCount(contents: ReviewTabContents): number {
  return contents.duplicateGroups.reduce((total, group) => total + group.rows.length, 0);
}

export function tabCount(contents: ReviewTabContents, tab: ReviewTab): number {
  if (tab === 'all') return contents.all.length;
  if (tab === 'needs-fixing') return contents.needsFixing.length;
  if (tab === 'duplicates') return duplicateRowCount(contents) + contents.exactCopies.length;
  return contents.ready.length;
}

export function initialReviewTab(contents: ReviewTabContents): ReviewTab {
  if (contents.needsFixing.length > 0) return 'needs-fixing';
  if (duplicateRowCount(contents) > 0) return 'duplicates';
  return 'all';
}

const TAB_LABEL: Record<ReviewTab, string> = { all: 'All rows', 'needs-fixing': 'Needs fixing', duplicates: 'Duplicates', ready: 'Ready' };
const HIDDEN_ONLY_LABEL = 'Hidden';
const DUPLICATES_AND_HIDDEN_LABEL = 'Duplicates & hidden';

/** The pill text for a tab. The duplicates tab also lists the rows the
 *  uploaded workbook hides, which are not duplicates, so its name follows what
 *  it holds: "Duplicates" for duplicates and exact copies, "Hidden" when every
 *  row in it is a hidden one, "Duplicates & hidden" when it holds both. */
export function tabLabel(contents: ReviewTabContents, tab: ReviewTab): string {
  if (tab !== 'duplicates') return TAB_LABEL[tab];
  const hidden = contents.exactCopies.filter((copy) => copy.reason === 'hidden_in_sheet').length;
  if (hidden === 0) return TAB_LABEL.duplicates;
  return hidden === tabCount(contents, 'duplicates') ? HIDDEN_ONLY_LABEL : DUPLICATES_AND_HIDDEN_LABEL;
}
